import React, { forwardRef } from "react";

/* ---------------- Sparkline ----------------
   Tone colors the stroke/fill of SVG sub-paths, so it stays a lookup map. */

/**
 * Sparkline stroke/fill palette. `neutral` is the muted "no-signal" tone
 * (matches the `none` status dot, `bg-pb-border-strong`) — exported so callers
 * mapping a `KpiStatus` to a sparkline tone can name it (a KpiStatus of `none`
 * has no semantic colour; it must read as "no data", not cyan `info`).
 */
export type SparkTone =
  | "healthy"
  | "watch"
  | "critical"
  | "ai"
  | "info"
  | "neutral";

export interface SparklineProps extends Omit<
  React.SVGProps<SVGSVGElement>,
  "width" | "height" | "fill"
> {
  data: number[];
  width?: number;
  height?: number;
  tone?: SparkTone;
  fill?: boolean;
  /** Use a stronger fill opacity (for explicit area-chart usage). */
  strongFill?: boolean;
}

const SPARK_STROKE: Record<SparkTone, string> = {
  healthy: "stroke-pb-green",
  watch: "stroke-pb-amber",
  critical: "stroke-pb-red",
  ai: "stroke-pb-iris",
  info: "stroke-pb-cyan",
  neutral: "stroke-pb-border-strong",
};

const SPARK_FILL: Record<SparkTone, string> = {
  healthy: "fill-pb-green/15",
  watch: "fill-pb-amber/15",
  critical: "fill-pb-red/15",
  ai: "fill-pb-iris/15",
  info: "fill-pb-cyan/15",
  neutral: "fill-pb-border-strong/15",
};

const SPARK_FILL_STRONG: Record<SparkTone, string> = {
  healthy: "fill-pb-green/30",
  watch: "fill-pb-amber/30",
  critical: "fill-pb-red/30",
  ai: "fill-pb-iris/30",
  info: "fill-pb-cyan/30",
  neutral: "fill-pb-border-strong/30",
};

export const Sparkline = forwardRef<SVGSVGElement, SparklineProps>(
  function Sparkline(
    {
      data,
      width = 100,
      height = 28,
      tone = "healthy",
      fill,
      strongFill,
      className,
      ...rest
    },
    ref,
  ) {
    if (data.length === 0) return null;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = data.length > 1 ? width / (data.length - 1) : 0;
    const points = data.map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * height;
      return [x, y] as const;
    });
    const path = points
      .map(
        ([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`,
      )
      .join(" ");
    const area = `${path} L ${width} ${height} L 0 ${height} Z`;
    return (
      <svg
        ref={ref}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className={className}
        {...rest}
      >
        {fill && (
          <path
            d={area}
            className={strongFill ? SPARK_FILL_STRONG[tone] : SPARK_FILL[tone]}
          />
        )}
        <path
          d={path}
          fill="none"
          strokeWidth={1.5}
          className={SPARK_STROKE[tone]}
        />
      </svg>
    );
  },
);
